/**
 * SaveInvestigationButton — renders a "Save Investigation" action that writes
 * a full deep snapshot to /api/investigations. Used on Fix / Fit panels.
 */
import React, { useState } from 'react';
import TagChips from './TagChips';
import { VisibilityBadge } from './VisibilityToggle';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SaveInvestigationButton({
  companyData,
  analysisLens,
  candidates,
  analysisResult,
  fixerResult,
  fitResult,
  style,
}) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [headline, setHeadline] = useState('');
  const [tags, setTags] = useState([]);
  const [isPrivate, setIsPrivate] = useState(false);

  if (!companyData) return null;

  const scopeLabel = [companyData.vector, companyData.sub_vector].filter(Boolean).join(' / ') || null;

  const save = async () => {
    setSaving(true); setErr(null); setMsg(null);
    try {
      const payload = {
        company_id: companyData.id,
        company_name: companyData.name,
        vector: companyData.vector || null,
        sub_vector: companyData.sub_vector || null,
        analysis_lens: analysisLens || null,
        scope_label: scopeLabel,
        headline_summary: headline.trim() || (fitResult ? 'Scout + Analyse + Fix + Fit snapshot'
          : fixerResult ? 'Scout + Analyse + Fix snapshot'
          : 'Analysis snapshot'),
        tags,
        visibility: isPrivate ? 'private' : 'shared',
        approved_signals: (candidates || []).filter((c) => c.status === 'approved' || c.approved),
        analysis: analysisResult || null,
        fixer: fixerResult || null,
        fit: fitResult || null,
      };
      const res = await fetch(`${API_URL}/api/investigations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(e.detail) || 'Save failed');
      }
      setMsg(`Investigation saved${isPrivate ? ' (private)' : ''}.`);
      setHeadline('');
      setTags([]);
      setIsPrivate(false);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="save-investigation-panel"
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
        padding: 12, margin: '12px 0 0',
        background: 'var(--surface)',
        border: '1px solid var(--border)', borderRadius: 8, ...style,
      }}
    >
      <input
        type="text"
        value={headline}
        onChange={(e) => setHeadline(e.target.value)}
        placeholder="Optional headline (e.g. Q1 review — Azure migration stall)"
        data-testid="save-investigation-headline"
        style={{ flex: '1 1 260px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
      />
      <button
        className="btn btn-primary btn-sm"
        disabled={saving}
        onClick={save}
        data-testid="save-investigation-button"
      >
        {saving ? 'Saving…' : 'Save Investigation'}
      </button>
      <label
        data-testid="save-investigation-private-toggle"
        title="Only you will see it. You can change this later from the snapshot page."
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <input
          type="checkbox"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
          data-testid="save-investigation-private-checkbox"
          style={{ margin: 0 }}
        />
        Save as private
        {isPrivate && <VisibilityBadge visibility="private" compact testid="save-inv-preview-private-badge" />}
      </label>
      {msg && <span data-testid="save-investigation-success" style={{ fontSize: 11, color: 'var(--success)' }}>{msg}</span>}
      {err && <span data-testid="save-investigation-error" style={{ fontSize: 11, color: 'var(--danger)' }}>{err}</span>}
      <div style={{ width: '100%', marginTop: 2 }}>
        <TagChips
          tags={tags}
          editable
          onChange={(newTags) => { setTags(newTags); return Promise.resolve(); }}
          testidPrefix="save-inv-tag"
        />
      </div>
    </div>
  );
}
